import { useEffect, useState, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, query, where, serverTimestamp, deleteDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Trophy, Gamepad2, Award, BarChart3, Settings, ShoppingBag } from "lucide-react";
import { auth, db, functions } from "./firebase-config";
import Auth from "./components/Auth";
import Chat from "./components/Chat";
import Results from "./components/Results";
import DailyStats from "./components/DailyStats";
import Profile from "./components/Profile";
import Leaderboard from "./components/Leaderboard";
import Admin from "./components/Admin";
import Shop from "./components/Shop";
import TutorialGame from "./components/TutorialGame";
import QuestionPrompt from "./components/QuestionPrompt";
import PayoffMatrixGrid from "./components/PayoffMatrixGrid";
import Logo from "./components/Logo";
import AvatarRing from "./components/AvatarRing";
import SubtitleBadge from "./components/SubtitleBadge";
import { getEquippedItem, getShopBalance } from "./cosmetics";
import { TRUST_COLOR, TEXT_MUTED, BORDER } from "./theme";
import { DISABLE_DAILY_LIMIT } from "./config";

function msUntilNextLocalMidnight() {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return next - now;
}

function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Must match the email hardcoded in firestore.rules' isAdmin() check.
const ADMIN_EMAIL = "a_e_driver@btinternet.com";

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // Game docs are kept around (not deleted) until midnight cleanup so daily
  // stats can count them, which means a player can match several "playerA ==
  // me" / "playerB == me" docs at once (today's earlier finished games plus
  // a brand new one). We keep the raw lists and pick the right one to show
  // below, rather than trusting whichever doc an unordered query returns first.
  const [gamesAsA, setGamesAsA] = useState([]);
  const [gamesAsB, setGamesAsB] = useState([]);
  const [dismissedGameIds, setDismissedGameIds] = useState(new Set());
  const [waiting, setWaiting] = useState(false);
  const [waitingRoomDoc, setWaitingRoomDoc] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [statsShown, setStatsShown] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [shopItems, setShopItems] = useState([]);
  const [hasPlayedToday, setHasPlayedToday] = useState(false);
  // Unlike hasPlayedToday (which is forced false in test mode so the daily
  // queue limit doesn't block replays), this always reflects reality — it
  // gates access to daily stats regardless of the testing toggle.
  const [playedToday, setPlayedToday] = useState(false);
  const [todayPreview, setTodayPreview] = useState(null); // { rules, hasQuestion, question }
  const [answeringQuestion, setAnsweringQuestion] = useState(false);
  const [countdown, setCountdown] = useState(msUntilNextLocalMidnight());
  // Game IDs we've actually watched be in "chat" state this session. A page
  // refresh that lands directly on an already-finished game never added that
  // id here, which is how we tell "I was just watching this end" (show the
  // reveal) apart from "I reloaded onto an old finished game" (go to menu).
  const liveGameIdsRef = useRef(new Set());

  // Pick the game to show: an active chat always wins (you can't dismiss a
  // live match). Otherwise look only at the single most recently created
  // game — if that one has already been dismissed, show the menu. We never
  // fall back to an older finished game, since today's earlier games are
  // kept around purely for stats and shouldn't resurface as "go to Results"
  // detours once the player has moved past them.
  const candidates = [...gamesAsA, ...gamesAsB];
  const activeGame = candidates.find((g) => g.state === "chat");
  const mostRecent = [...candidates].sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0))[0];
  const game = activeGame || (mostRecent && !dismissedGameIds.has(mostRecent.id) ? mostRecent : null);

  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, []);

  // Accurate "today's rules" preview for the menu — calls the Cloud Function
  // rather than computing it locally, so a private dailyConfig override (or
  // an auto-generated random day) is reflected before you've even matched.
  useEffect(() => {
    if (!user) return;
    httpsCallable(functions, "getTodayPreview")()
      .then((result) => setTodayPreview(result.data))
      .catch((error) => console.error("Error loading today's preview:", error));
  }, [user]);

  // Load user profile
  useEffect(() => {
    if (!user) return;
    
    const unsubscribe = onSnapshot(doc(db, "users", user.uid), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setUserProfile(data);

        const isSameDay = data.lastPlayedDate
          ? data.lastPlayedDate.toDate().toDateString() === new Date().toDateString()
          : false;
        setPlayedToday(isSameDay);
        setHasPlayedToday(!DISABLE_DAILY_LIMIT && isSameDay);
      }
    });

    return unsubscribe;
  }, [user]);

  // Listen for games
  useEffect(() => {
    if (!user) return;

    const qA = query(collection(db, "games"), where("playerA", "==", user.uid));
    const qB = query(collection(db, "games"), where("playerB", "==", user.uid));

    const unsubA = onSnapshot(qA, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.forEach((g) => { if (g.state === "chat") liveGameIdsRef.current.add(g.id); });
      setGamesAsA(docs);
    });

    const unsubB = onSnapshot(qB, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.forEach((g) => { if (g.state === "chat") liveGameIdsRef.current.add(g.id); });
      setGamesAsB(docs);
    });

    return () => { unsubA(); unsubB(); };
  }, [user]);

  // Tick the "next game" countdown shown once the daily limit is hit.
  useEffect(() => {
    const interval = setInterval(() => setCountdown(msUntilNextLocalMidnight()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Shop catalogue — readable by everyone, needed wherever an equipped
  // cosmetic (subtitle/border/bubble) has to be resolved from an itemId.
  useEffect(() => {
    if (!user) return;
    return onSnapshot(collection(db, "shopItems"), (snap) => {
      setShopItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [user]);

  // Once a real match is found, stop waiting and clear the waiting-room entry
  useEffect(() => {
    if (!game || !waitingRoomDoc) return;
    setWaiting(false);
    deleteDoc(doc(db, "waitingRoom", waitingRoomDoc)).catch(console.error);
    setWaitingRoomDoc(null);
  }, [game, waitingRoomDoc]);

  // A finished game we never watched live (see liveGameIdsRef above) is
  // never shown, but it still counts as "game" until dismissed — left alone,
  // it would sit there forever and trick the effect above into thinking a
  // brand new match was found the instant you next queue, instantly
  // cancelling your wait. Dismiss it immediately so "game" stays null until
  // an actual new match arrives.
  useEffect(() => {
    if (game && game.state === "results" && !liveGameIdsRef.current.has(game.id) && !dismissedGameIds.has(game.id)) {
      setDismissedGameIds((prev) => new Set(prev).add(game.id));
    }
  }, [game, dismissedGameIds]);

  const startGame = async (answer = null) => {
    setWaiting(true);
    await setDoc(doc(db, "waitingRoom", user.uid), {
      uid: user.uid,
      joinedAt: serverTimestamp(),
      answer,
    });
    setWaitingRoomDoc(user.uid);
  };

  // Today's question (if there is one) has to be answered before queueing —
  // asking it mid-match would eat into the fixed 3-minute chat window, since
  // both players' answers are needed before the matchmaker can stamp them
  // onto the game doc.
  const handlePlayClick = () => {
    if (todayPreview?.question) {
      setAnsweringQuestion(true);
    } else {
      startGame();
    }
  };

  const handleQuestionAnswer = (choice) => {
    setAnsweringQuestion(false);
    startGame(choice);
  };

  const cancelWaiting = async () => {
    if (waitingRoomDoc) {
      await deleteDoc(doc(db, "waitingRoom", waitingRoomDoc));
      setWaitingRoomDoc(null);
    }
    setWaiting(false);
  };

  const returnToMenu = async (gameResult) => {
    // Update user's last played date and save game history
    if (gameResult) {
      const today = new Date();
      const dateKey = today.toISOString().split('T')[0]; // YYYY-MM-DD
      
      await updateDoc(doc(db, "users", user.uid), {
        lastPlayedDate: serverTimestamp(),
        [`gameHistory.${dateKey}`]: {
          myMove: gameResult.myMove,
          opponentMove: gameResult.opponentMove,
          myScore: gameResult.myScore,
          opponentScore: gameResult.opponentScore,
          timestamp: serverTimestamp(),
        }
      });

      // Show stats after the first game of this session (hasPlayedToday is
      // always false in test mode, so that alone can't gate this — without
      // statsShown this would re-trigger on every single continue click).
      if (!hasPlayedToday && !statsShown) {
        setShowStats(true);
        setStatsShown(true);
      }
    }

    if (game) {
      setDismissedGameIds((prev) => new Set(prev).add(game.id));
    }
  };

  if (!user || !userProfile) return <Auth />;
  
  if (showProfile) {
    return <Profile user={user} userProfile={userProfile} shopItems={shopItems} onBack={() => setShowProfile(false)} />;
  }

  if (showLeaderboard) {
    return <Leaderboard onClose={() => setShowLeaderboard(false)} currentUser={user} userProfile={userProfile} shopItems={shopItems} />;
  }

  if (showAdmin) {
    return <Admin onClose={() => setShowAdmin(false)} />;
  }

  if (showShop) {
    return <Shop userProfile={userProfile} shopItems={shopItems} onClose={() => setShowShop(false)} />;
  }

  if (showStats) {
    return <DailyStats onClose={() => setShowStats(false)} userProfile={userProfile} shopItems={shopItems} />;
  }

  if (showTutorial) {
    return (
      <TutorialGame
        userProfile={userProfile}
        onClose={() => setShowTutorial(false)}
        onPlay={() => { setShowTutorial(false); handlePlayClick(); }}
      />
    );
  }

  // Only show the reveal if we actually watched this game be "chat" this
  // session — a refresh landing directly on an already-finished game skips
  // straight to the menu instead of replaying/re-triggering the reveal.
  if (game && game.state === "results" && liveGameIdsRef.current.has(game.id)) {
    return <Results user={user} userProfile={userProfile} game={game} shopItems={shopItems} returnToMenu={returnToMenu} />;
  }

  if (game && game.state === "chat") return <Chat user={user} userProfile={userProfile} gameId={game.id} shopItems={shopItems} />;

  if (answeringQuestion) {
    return <QuestionPrompt question={todayPreview.question} onAnswer={handleQuestionAnswer} />;
  }

  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div style={{ width: "100%", maxWidth: "380px" }}>
        <div style={{ marginBottom: "20px" }}>
          <Logo size={36} />
        </div>

        {/* Profile card */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 14px", border: `1px solid ${BORDER}`, borderRadius: "10px", marginBottom: "10px", background: "#fff", cursor: "pointer" }} onClick={() => setShowProfile(true)}>
          <AvatarRing photoURL={userProfile.photoURL} size={42} item={getEquippedItem(userProfile, shopItems, "border")} />
          <div style={{ textAlign: "left", flex: 1 }}>
            <div style={{ fontWeight: "700", fontSize: "0.875rem", display: "flex", alignItems: "center", gap: "6px" }}>
              {userProfile.displayName}
              <SubtitleBadge item={getEquippedItem(userProfile, shopItems, "subtitle")} />
            </div>
            <div style={{ display: "flex", gap: "12px", fontSize: "0.6875rem", color: TEXT_MUTED, marginTop: "2px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Trophy size={12} /> {userProfile.totalScore || 0} pts</span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Gamepad2 size={12} /> {userProfile.gamesPlayed || 0} games</span>
            </div>
          </div>
          {user.email === ADMIN_EMAIL && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowAdmin(true); }}
              title="admin"
              style={{ background: "none", border: "none", padding: "6px", display: "flex" }}
            >
              <Settings size={18} color={TEXT_MUTED} />
            </button>
          )}
        </div>

        {/* Leaderboard / daily stats / shop */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "14px" }}>
          <button
            onClick={() => setShowLeaderboard(true)}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px", padding: "10px 4px", background: "#fff", color: "#1a1a2e", border: `1px solid ${BORDER}`, borderRadius: "8px", fontWeight: "600", fontSize: "0.6875rem", whiteSpace: "nowrap" }}
          >
            <Award size={14} color={TEXT_MUTED} />
            leaderboard
          </button>
          <button
            onClick={() => playedToday && setShowStats(true)}
            disabled={!playedToday}
            title={playedToday ? "" : "play today's game to unlock"}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px", padding: "10px 4px", background: "#fff", color: "#1a1a2e", border: `1px solid ${BORDER}`, borderRadius: "8px", fontWeight: "600", fontSize: "0.6875rem", whiteSpace: "nowrap" }}
          >
            <BarChart3 size={14} color={TEXT_MUTED} />
            today's stats
          </button>
          <button
            onClick={() => setShowShop(true)}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px", padding: "10px 4px", background: "#fff", color: "#1a1a2e", border: `1px solid ${BORDER}`, borderRadius: "8px", fontWeight: "600", fontSize: "0.6875rem", whiteSpace: "nowrap" }}
          >
            <ShoppingBag size={14} color={TEXT_MUTED} />
            shop{getShopBalance(userProfile) > 0 ? ` · ${getShopBalance(userProfile)}` : ""}
          </button>
        </div>

        {DISABLE_DAILY_LIMIT && (
          <div style={{ border: `1px solid ${BORDER}`, padding: "8px 10px", borderRadius: "8px", marginBottom: "12px", fontSize: "0.6875rem", color: TEXT_MUTED, textAlign: "center" }}>
            testing mode: daily limit disabled
          </div>
        )}

        <div style={{ marginBottom: "14px" }}>
          {todayPreview ? (
            <PayoffMatrixGrid rules={todayPreview.rules} />
          ) : (
            <div style={{ textAlign: "center", padding: "20px", fontSize: "0.8125rem", color: TEXT_MUTED }}>
              loading today's rules...
            </div>
          )}
        </div>

        {hasPlayedToday && !DISABLE_DAILY_LIMIT ? (
          <button disabled style={{ padding: "14px", fontSize: "0.9375rem", background: "#eceef3", color: TEXT_MUTED, border: "none", borderRadius: "10px", width: "100%", fontWeight: "700", cursor: "not-allowed" }}>
            next game in {formatCountdown(countdown)}
          </button>
        ) : waiting ? (
          <div style={{ textAlign: "center" }}>
            <div className="spinner" style={{ margin: "0 auto 12px", width: "32px", height: "32px", border: `3px solid ${BORDER}`, borderTop: `3px solid ${TRUST_COLOR}`, borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
            <p style={{ fontSize: "0.8125rem", color: TEXT_MUTED, marginBottom: "12px" }}>searching for an opponent...</p>
            <button onClick={cancelWaiting} style={{ padding: "8px 18px", fontSize: "0.8125rem", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px" }}>
              cancel
            </button>
          </div>
        ) : (
          <>
            <button onClick={handlePlayClick} disabled={!todayPreview} style={{ padding: "14px", fontSize: "0.9375rem", background: todayPreview ? TRUST_COLOR : "#eceef3", color: "white", border: "none", borderRadius: "10px", width: "100%", fontWeight: "700" }}>
              play the game
            </button>
            <button
              onClick={() => setShowTutorial(true)}
              style={{ marginTop: "8px", padding: "10px", fontSize: "0.8125rem", background: "none", color: TEXT_MUTED, border: "none", width: "100%", fontWeight: "600" }}
            >
              how to play
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default App;